import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface TenantContext {
  slug: string;
  name?: string;
}

const STORAGE_KEY = 'tenant_context';

@Injectable({
  providedIn: 'root'
})
export class TenantContextService {
  private tenant$ = new BehaviorSubject<TenantContext | null>(this.detectTenant());

  constructor() { }

  /** ✅ Get observable tenant context */
  getTenant$(): Observable<TenantContext | null> {
    return this.tenant$.asObservable();
  }

  /** ✅ Get current tenant synchronously */
  getCurrentTenant(): TenantContext | null {
    return this.tenant$.getValue();
  }

  /** ✅ Get only slug */
  getTenantSlug(): string | null {
    return this.getCurrentTenant()?.slug ?? null;
  }

  /** ✅ Set tenant manually (stored securely) */
  setTenant(context: TenantContext | null): void {
    if (context) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
    this.tenant$.next(context);
  }

  /** ✅ Clear tenant (logout or tenant switch) */
  clearTenant(): void {
    this.setTenant(null);
  }

  /** ✅ Detect tenant from URL or session */
  private detectTenant(): TenantContext | null {
    // 1️⃣ Check session storage first
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed: TenantContext = JSON.parse(stored);
        if (parsed.slug) return parsed;
      } catch {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }

    // 2️⃣ Path-based detection: /{tenant}/login
    const pathSegments = window.location.pathname
      .split('/')
      .filter(Boolean);

    if (
      pathSegments.length > 0 &&
      !['login', 'assets'].includes(pathSegments[0])
    ) {
      const slug = pathSegments[0].toLowerCase();
      const context = { slug };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
      return context;
    }


    // 2️⃣ Detect from subdomain
    /*const hostname = window.location.hostname; // e.g. capemind.efficx.com or localhost
    const parts = hostname.split('.');

    if (parts.length > 2) {
      const slug = parts[0]; // e.g. capemind
      const context = { slug };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
      return context;
    } */

    // 3️⃣ Detect from query param (for localhost)
    const params = new URLSearchParams(window.location.search);
    const slugFromQuery = params.get('tenant');

    if (slugFromQuery) {
      const context = { slug: slugFromQuery.toLowerCase() };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
      return context;
    }

    // 4️⃣ No tenant found
    return null;
  }
}
